import { useState } from "react";
import { toast } from "sonner";
import { Lock, FileDown, Send, Loader2, AlarmClock, PiggyBank } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useApi, useLoad, useOwedUnlocked, owedToken } from "@/lib/api";
import { useMe } from "@/lib/me";
import { gbp } from "@/lib/format";
import { PageHead, StatusBadge, Empty } from "@/components/Bits";
import QueryDrawer from "@/components/QueryDrawer";

const STALE = 14;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

async function download(api, path, name) {
  try {
    const blob = await api("get", path, null, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = name; a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error(e.message); }
}

function Unlock({ email }) {
  const api = useApi();
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api("post", "/stepup/start", { purpose: "owed" }); setSent(true); setCode(""); toast.success(`Code sent to ${email}`); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const verify = async (v) => {
    setBusy(true);
    try { const r = await api("post", "/stepup/verify", { code: v, purpose: "owed" }); owedToken.set(r.token, r.expires_at); toast.success("Unlocked"); }
    catch (e) { toast.error(e.message); setCode(""); } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto mt-6 max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center rise" data-testid="overdue-locked">
      <Lock className="mx-auto h-9 w-9 text-rose-500" />
      <h2 className="mt-4 font-display text-2xl font-extrabold">Money owed back is locked</h2>
      <p className="mt-2 text-sm text-slate-600">For privacy, owed amounts are hidden until you enter a code sent to your email. They stay visible until you close this tab or sign out.</p>
      {!sent ? (
        <Button className="mt-6 w-full" onClick={send} disabled={busy} data-testid="overdue-send-code-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Email me a code</Button>
      ) : (
        <div className="mt-6 flex flex-col items-center gap-4">
          <InputOTP maxLength={6} value={code} disabled={busy} onChange={(v) => { setCode(v.replace(/\D/g, "")); if (v.length === 6 && /^\d{6}$/.test(v)) verify(v); }} data-testid="overdue-code-input">
            <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
          </InputOTP>
          <button type="button" onClick={send} disabled={busy} className="text-sm text-slate-500 underline" data-testid="overdue-resend-code">Send a new code</button>
        </div>
      )}
    </div>
  );
}

function Confirm({ ask, onCancel, onGo }) {
  return (
    <AlertDialog open={!!ask} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="chase-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Send a chaser?</AlertDialogTitle>
          <AlertDialogDescription data-testid="chase-confirm-text">{ask?.text}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="chase-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onGo} data-testid="chase-confirm-send">Send email</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const Waiting = ({ d }) => (
  <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${d >= STALE ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>Waiting {d} day{d === 1 ? "" : "s"}</span>
);

function Claim({ i, owner, chased, onOpen, onChase }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center" data-testid={`owed-item-${i.thread}`}>
      <button onClick={() => onOpen(i.thread)} className="min-w-0 flex-1 text-left" data-testid={`owed-item-open-${i.thread}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{i.supplier}</span>
          <span className="font-mono text-lg font-bold text-rose-600" data-testid={`owed-item-amount-${i.thread}`}>{gbp(i.at_stake)}</span>
        </div>
        <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="font-mono">{i.thread} · {i.month}</span><StatusBadge status={i.status} /><Waiting d={i.days_waiting} />
          {chased && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700" data-testid={`owed-item-chased-${i.thread}`}>Chased just now</span>}
        </p>
      </button>
      {owner && <Button size="sm" variant="outline" onClick={() => onChase(i)} data-testid={`owed-chase-${i.thread}`}><Send className="mr-1 h-4 w-4" />Chase this claim</Button>}
    </div>
  );
}

function Suppliers({ rows, owner, onPdf }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <table className="w-full text-sm" data-testid="owed-suppliers-table">
        <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
          <tr><th className="p-3">Wholesaler</th><th className="p-3 text-right">Claims</th><th className="p-3 text-right">Owed back</th>{owner && <th className="p-3" />}</tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.supplier} className="border-t border-slate-100" data-testid={`owed-supplier-${slug(s.supplier)}`}>
              <td className="p-3 font-semibold">{s.supplier}</td><td className="p-3 text-right">{s.count}</td>
              <td className="p-3 text-right font-mono font-semibold text-rose-600">{gbp(s.total)}</td>
              {owner && <td className="p-3 text-right"><Button size="icon" variant="ghost" onClick={() => onPdf(s.supplier)} title={`PDF for ${s.supplier}`} data-testid={`owed-supplier-pdf-${slug(s.supplier)}`}><FileDown className="h-4 w-4" /></Button></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Owed({ owner }) {
  const api = useApi();
  const o = useLoad(`/owed?older_than_days=${STALE}`);
  const [staleOnly, setStaleOnly] = useState(false);
  const [open, setOpen] = useState(null);
  const [ask, setAsk] = useState(null);
  const [chased, setChased] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const d = o.data;
  if (!d) return <p className="text-sm text-slate-400" data-testid="owed-loading">Loading…</p>;
  const recent = (i) => chased.has(i.thread) || (i.last_chased_at && Date.now() - new Date(i.last_chased_at) < 864e5);
  const stale = d.items.filter((i) => i.days_waiting >= STALE);
  const due = stale.filter((i) => !recent(i));
  const shown = staleOnly ? stale : d.items;
  const mark = (ts) => setChased((s) => new Set([...s, ...ts]));
  const go = async () => {
    const a = ask; setAsk(null); setBusy(true);
    try {
      if (a.one) {
        const r = await api("post", `/queries/${a.one.thread}/chase`, {});
        if (r.email === "sent") { mark([a.one.thread]); toast.success(`Chased ${a.one.supplier} about ${a.one.thread}`); } else toast.error(`Not sent: ${r.email.replace(/^not sent:\s*/i, "")}`);
      } else {
        const r = await api("post", "/queries/chase", { older_than_days: STALE });
        const bad = new Set(r.failed.map((f) => f.thread));
        mark(due.filter((i) => !bad.has(i.thread)).map((i) => i.thread));
        toast[r.failed.length ? "warning" : "success"](`${r.sent} chaser${r.sent === 1 ? "" : "s"} sent${r.skipped ? `, ${r.skipped} skipped (chased in the last 24h)` : ""}${r.failed.length ? `, ${r.failed.length} failed` : ""}`);
      }
      o.reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const suppliersOf = new Set(due.map((i) => i.supplier)).size;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2" data-testid="owed-actions">
        <Button variant="outline" onClick={() => download(api, "/owed.pdf", "owed-back-all.pdf")} data-testid="owed-pdf-all-button"><FileDown className="mr-1 h-4 w-4" />Share as PDF</Button>
        {owner && <Button disabled={!due.length || busy} onClick={() => setAsk({ text: `Email ${suppliersOf} wholesaler${suppliersOf === 1 ? "" : "s"} about ${due.length} claim${due.length === 1 ? "" : "s"} waiting ${STALE}+ days (${gbp(due.reduce((t, i) => t + i.at_stake, 0))})?` })} data-testid="owed-chase-stale-button">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <AlarmClock className="mr-1 h-4 w-4" />}Chase {STALE}+ days ({due.length})</Button>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6" data-testid="owed-total-card">
          <p className="text-xs font-extrabold uppercase tracking-wider text-rose-700">Still owed back</p>
          <p className="mt-1 font-display text-4xl font-extrabold text-rose-600" data-testid="owed-total">{gbp(d.total)}</p>
          <p className="mt-1 text-sm text-slate-600" data-testid="owed-count">{d.count} open quer{d.count === 1 ? "y" : "ies"} with money owed</p>
        </div>
        <button onClick={() => setStaleOnly(!staleOnly)} className={`rounded-3xl border p-6 text-left transition-colors ${staleOnly ? "border-amber-400 bg-amber-100" : "border-amber-200 bg-amber-50 hover:border-amber-400"}`} data-testid="owed-stale-card">
          <p className="text-xs font-extrabold uppercase tracking-wider text-amber-700">{staleOnly ? "Showing only these · tap to show all" : "Tap to show only these"}</p>
          <p className="mt-1 font-display text-2xl font-extrabold text-amber-700" data-testid="owed-stale-summary">{d.stale_count} waiting {STALE}+ days · {gbp(d.stale_total)}</p>
        </button>
      </div>
      {d.count === 0 ? <Empty icon={PiggyBank} title="Nothing owed back" text="Every claim with money at stake has been settled." testid="owed-empty" /> : (
        <>
          <Suppliers rows={d.suppliers} owner={owner} onPdf={(s) => download(api, `/owed.pdf?supplier=${encodeURIComponent(s)}`, `owed-back-${slug(s)}.pdf`)} />
          <div className="space-y-2" data-testid="owed-items">
            {shown.map((i) => <Claim key={i.thread} i={i} owner={owner} chased={chased.has(i.thread)} onOpen={setOpen}
              onChase={(x) => setAsk({ one: x, text: `Email ${x.supplier} again about ${x.thread} (${gbp(x.at_stake)})?` })} />)}
          </div>
        </>
      )}
      <Confirm ask={ask} onCancel={() => setAsk(null)} onGo={go} />
      <QueryDrawer thread={open} onClose={() => setOpen(null)} onChanged={o.reload} />
    </div>
  );
}

export default function OverduePage() {
  const me = useMe();
  const unlocked = useOwedUnlocked();
  return (
    <div data-testid="overdue-page">
      <PageHead title="Overdue" sub="Money wholesalers still owe back, biggest amounts first, so you know who to chase.">
        {unlocked && <Button variant="ghost" onClick={() => owedToken.set("")} data-testid="overdue-lock-button"><Lock className="mr-1 h-4 w-4" />Lock</Button>}
      </PageHead>
      {unlocked ? <Owed owner={me.role === "owner"} /> : <Unlock email={me.email} />}
    </div>
  );
}
